/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registry as defaultRegistry } from '@/lib/page-designer/registry';
import { initializeRegistry } from '@/lib/page-designer/static-registry';
import { readServerRenderedComponentTypes } from '@/lib/page-designer/component-types';

type Importer = () => Promise<{ default?: unknown }>;

/** Hydration must never be blocked indefinitely by a slow or failing chunk. */
const DEFAULT_TIMEOUT_MS = 3000;

/**
 * Collects the component importers from the generated static registry without touching the auto-generated file:
 * `initializeRegistry` accepts any target exposing `registerImporter`.
 */
function collectImporters(): Map<string, Importer> {
    const importers = new Map<string, Importer>();
    const collector = {
        registerImporter: (id: string, importer: Importer) => importers.set(id, importer),
    } as unknown as Parameters<typeof initializeRegistry>[0];
    initializeRegistry(collector);
    return importers;
}

/**
 * Client-only: before hydration, loads the modules of the Page Designer components the server rendered and
 * registers them as eager (`raw`) components.
 *
 * Why: the registry otherwise returns a `React.lazy` wrapper that suspends on first render. A component that
 * suspends mid-hydration and then receives an update has its server HTML discarded and re-rendered on the client —
 * visible as the content disappearing and reappearing (layout shift), e.g. the top header banner on every route.
 *
 * Failures and slow chunks are tolerated: anything not ready within the timeout falls back to the lazy path.
 */
export async function preloadServerRenderedComponents({
    doc = document,
    registry = defaultRegistry,
    timeoutMs = DEFAULT_TIMEOUT_MS,
}: {
    doc?: Document;
    registry?: typeof defaultRegistry;
    timeoutMs?: number;
} = {}): Promise<void> {
    const types = readServerRenderedComponentTypes(doc);
    if (types.length === 0) return;

    initializeRegistry(registry);
    const importers = collectImporters();

    const preloadAll = Promise.allSettled(
        types.map(async (type) => {
            const importer = importers.get(type);
            if (!importer) return;
            // Build the regular entry first (keeps fallback/loader metadata), then promote the loaded module to
            // the eager component so getComponent() returns it without suspending.
            await registry.preload(type);
            const mod = await importer();
            if (mod.default) registry.registerComponent(type, mod.default as never);
        })
    );

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
    });
    await Promise.race([preloadAll, timeout]);
    clearTimeout(timer);
}
